import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    Box,
    FormControl,
    Typography,
    TextField,
    Switch,
    Select,
    MenuItem,
    FormHelperText,
    IconButton,
} from '@mui/material';

import CronWrapper from '../../Cron/CronDialog';

import { timeZoneOptions } from '../../../helper/ScheduleService.const';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import RegexCode from '../../../../../types/constants/regex';
import { SCS } from './settingsTab.classes';

export const SettingsTab = () => {
    const {
        control,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        name: 'emails',
        control,
    });

    const cron = watch('cron');

    const { t } = useTranslation();

    const handleCronChange = (cronParam: string) => {
        setValue('cron', cronParam);
    };

    const handleAddEmail = () => {
        append('');
    };

    const handleRemoveEmail = (index: number) => {
        remove(index);
    };

    return (
        <Box className={SCS.root} sx={{ width: '600px' }}>
            <FormControl className={SCS.cronRow} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ marginRight: '48px', fontWeight: 'bold', height: '36px', alignContent: 'center' }}>
                    Cron:
                </Typography>
                <Box className={SCS.cronWrap} sx={{ width: '400px', marginLeft: 'auto' }}>
                    <div>
                        <CronWrapper onSave={handleCronChange} cronExpression={cron} />
                    </div>
                </Box>
            </FormControl>
            <FormControl className={SCS.queueRow} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ marginRight: '48px', fontWeight: 'bold', height: '36px', alignContent: 'center' }}>
                    {t('scheduleService.queueName')}
                </Typography>
                <Controller
                    name="queueName"
                    control={control}
                    rules={{
                        pattern: {
                            value: /^[a-z0-9_-]+$/,
                            message: t('scheduleService.queueNameValid'),
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            className={SCS.queueInput}
                            variant="outlined"
                            size="small"
                            disabled
                            sx={{ width: '400px', marginLeft: 'auto' }}
                            placeholder={t('scheduleService.enterQueueName')}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </FormControl>
            <FormControl
                className={SCS.enabledRow}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    mb: 2,
                    marginLeft: 'auto',
                }}
            >
                <Typography
                    sx={{
                        marginRight: '48px',
                        fontWeight: 'bold',
                        height: '36px',
                        alignContent: 'center',
                    }}
                >
                    {t('scheduleService.isEnabled')}
                </Typography>
                <Controller
                    name="isEnabled"
                    control={control}
                    render={({ field }) => (
                        <Box sx={{ marginLeft: 'auto', width: '410px' }}>
                            <Switch
                                className={SCS.enabledSwitch}
                                checked={!!field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        padding: '9px',
                                    },
                                }}
                            />
                        </Box>
                    )}
                />
            </FormControl>
            <FormControl
                className={SCS.authRow}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    mb: 2,
                }}
            >
                <Typography
                    sx={{
                        marginRight: '48px',
                        fontWeight: 'bold',
                        height: '36px',
                        alignContent: 'center',
                    }}
                >
                    {t('scheduleService.hasAuthentication')}
                </Typography>
                <Controller
                    name="hasAuthentication"
                    control={control}
                    render={({ field }) => (
                        <Box sx={{ marginLeft: 'auto', width: '410px' }}>
                            <Switch
                                className={SCS.authSwitch}
                                checked={!!field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        padding: '9px',
                                    },
                                }}
                            />
                        </Box>
                    )}
                />
            </FormControl>
            <FormControl className={SCS.timeZoneRow} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ marginRight: '48px', fontWeight: 'bold', height: '36px', alignContent: 'center' }}>
                    {t('scheduleService.timeZone')}
                </Typography>
                <Controller
                    name="timeZoneId"
                    control={control}
                    defaultValue=""
                    rules={{ required: t('scheduleService.requiredTimeZone') }}
                    render={({ field }) => (
                        <Box sx={{ marginLeft: 'auto' }}>
                            <Select
                                {...field}
                                className={SCS.timeZoneSelect}
                                sx={{ width: '400px', marginLeft: 'auto' }}
                                error={!!errors.timeZoneId}
                            >
                                <MenuItem value="">
                                    <em>{t('general.select')}</em>
                                </MenuItem>
                                {timeZoneOptions.map((tz: any) => (
                                    <MenuItem className={SCS.timeZoneOption} key={tz.value} value={tz.value} data-tz={tz.value}>
                                        {tz.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {!!errors.scheduleGroup && (
                                <FormHelperText sx={{ color: '#d32f2f' }}>
                                    {errors.timeZoneId ? String(errors.timeZoneId.message) : ''}
                                </FormHelperText>
                            )}
                        </Box>
                    )}
                />
            </FormControl>
            <FormControl className={SCS.emailsRow} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ marginRight: '48px', fontWeight: 'bold', height: '36px', alignContent: 'center' }}>
                    {t('scheduleService.emails')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto' }}>
                        {fields.map((item, index) => (
                            <Box
                                key={item.id}
                                className={SCS.emailRow}
                                data-email-index={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    mb: 2,
                                    gap: 2,
                                }}
                            >
                                <Controller
                                    key={item.id}
                                    name={`emails.${index}`}
                                    control={control}
                                    rules={{
                                        pattern: {
                                            value: RegexCode.emailValidation,
                                            message: t('scheduleService.invalidEmail'),
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className={SCS.emailInput}
                                            variant="outlined"
                                            size="small"
                                            error={
                                                !!(
                                                    errors.emails &&
                                                    Array.isArray(errors.emails) &&
                                                    errors.emails[index]?.message
                                                )
                                            }
                                            helperText={
                                                (errors.emails &&
                                                    Array.isArray(errors.emails) &&
                                                    errors.emails[index]?.message) ||
                                                ''
                                            }
                                            sx={{ width: '350px' }}
                                            placeholder={t('scheduleService.enterEmail')}
                                        />
                                    )}
                                />
                                <IconButton
                                    className={SCS.removeEmailButton}
                                    onClick={() => handleRemoveEmail(index)}
                                    color="error"
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            height: '18px',
                                            width: '18px',
                                        },
                                    }}
                                >
                                    <RemoveCircleIcon className={SCS.removeEmailIcon} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                    <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <IconButton
                            className={SCS.addEmailButton}
                            onClick={handleAddEmail}
                            color="primary"
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    height: '18px',
                                    width: '18px',
                                },
                            }}
                        >
                            <AddCircleIcon className={SCS.addEmailIcon} />
                        </IconButton>
                        <Typography
                            className={SCS.addEmailText}
                            onClick={handleAddEmail}
                            sx={{ ml: 1, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            {t('scheduleService.addEmail')}
                        </Typography>
                    </FormControl>
                </Box>
            </FormControl>
        </Box>
    );
};
